export type PublicRuntimeConfig = {
  chainId: number;
  chainName: string;
  rpcUrls: string[];
  readRpcUrl: string;
  explorerUrl: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  contracts: { mining: string; aethera: string; usdt: string; liquidityPool: string };
  reservedUserId: string;
};

declare global {
  interface Window {
    __AETHERA_RUNTIME_CONFIG__?: PublicRuntimeConfig;
  }
}

function required(name: string, value: string | undefined) {
  const normalized = value?.trim();
  if (!normalized) throw new Error(`Missing required environment variable: ${name}`);
  return normalized;
}

function positiveInteger(name: string, value: string | undefined) {
  const parsed = Number(required(name, value));
  if (!Number.isSafeInteger(parsed) || parsed <= 0) throw new Error(`${name} must be a positive integer.`);
  return parsed;
}

function serverRuntimeConfig(): PublicRuntimeConfig {
  const rpcUrls = required('APP_RPC_URLS', process.env.APP_RPC_URLS).split(',').map((value) => value.trim()).filter(Boolean);
  if (!rpcUrls.length) throw new Error('APP_RPC_URLS must contain at least one RPC URL.');
  return {
    chainId: positiveInteger('APP_CHAIN_ID', process.env.APP_CHAIN_ID),
    chainName: required('APP_CHAIN_NAME', process.env.APP_CHAIN_NAME),
    rpcUrls,
    readRpcUrl: required('APP_READ_RPC_URL', process.env.APP_READ_RPC_URL),
    explorerUrl: required('APP_EXPLORER_URL', process.env.APP_EXPLORER_URL).replace(/\/$/, ''),
    nativeCurrency: {
      name: required('APP_NATIVE_CURRENCY_NAME', process.env.APP_NATIVE_CURRENCY_NAME),
      symbol: required('APP_NATIVE_CURRENCY_SYMBOL', process.env.APP_NATIVE_CURRENCY_SYMBOL),
      decimals: positiveInteger('APP_NATIVE_CURRENCY_DECIMALS', process.env.APP_NATIVE_CURRENCY_DECIMALS),
    },
    contracts: {
      mining: required('MINING_CONTRACT_ADDRESS', process.env.MINING_CONTRACT_ADDRESS),
      aethera: required('AETHERA_TOKEN_ADDRESS', process.env.AETHERA_TOKEN_ADDRESS),
      usdt: required('USDT_TOKEN_ADDRESS', process.env.USDT_TOKEN_ADDRESS),
      liquidityPool: required('LIQUIDITY_POOL_ADDRESS', process.env.LIQUIDITY_POOL_ADDRESS),
    },
    reservedUserId: process.env.RESERVED_USER_ID?.trim() ?? '',
  };
}

export function getPublicRuntimeConfig() {
  if (typeof window !== 'undefined') {
    if (!window.__AETHERA_RUNTIME_CONFIG__) throw new Error('AETHERA runtime configuration was not loaded.');
    return window.__AETHERA_RUNTIME_CONFIG__;
  }
  return serverRuntimeConfig();
}

export const RUNTIME_CONFIG = new Proxy({} as PublicRuntimeConfig, {
  get(_target, property: keyof PublicRuntimeConfig) {
    return getPublicRuntimeConfig()[property];
  },
});
